import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errDetail, fmtUSD } from "@/lib/api";
import type { Booking, RefundQuote } from "@/types";

const ERR: Record<string, string> = {
  amount_exceeds_refundable: "El monto excede lo reembolsable.",
  refund_not_available: "Este pago no se puede reembolsar en línea (no es un pago Whop real). Reembólsalo manualmente o elige $0.",
  refund_provider_error: "Whop rechazó el reembolso. Revisa el pago en tu panel de Whop.",
  not_paid: "La reserva no tiene un pago registrado.",
};

export default function RefundDialog({ booking, onClose, onDone }: { booking: Booking | null; onClose: () => void; onDone: () => void }) {
  const open = !!booking;
  const { data: q } = useQuery<RefundQuote>({ queryKey: ["refund-quote", booking?.id], enabled: open, queryFn: async () => (await api.get(`/admin/bookings/${booking!.id}/refund-quote`)).data });
  const [choice, setChoice] = useState<"policy" | "full" | "none" | "custom">("policy");
  const [custom, setCustom] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  if (!booking) return null;
  const amount = !q ? 0 : choice === "policy" ? q.policy_amount : choice === "full" ? q.max_refundable : choice === "none" ? 0 : Math.min(Number(custom) || 0, q.max_refundable);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/bookings/${booking.id}/refund`, { amount, reason });
      toast.success(amount > 0 ? `Reserva cancelada y reembolso de ${fmtUSD(amount)} emitido` : "Reserva cancelada sin reembolso");
      onDone();
    } catch (e) {
      const d = errDetail(e);
      toast.error(ERR[d] || d);
    } finally {
      setSaving(false);
    }
  };
  const opt = (k: typeof choice, label: string, sub: string, testId: string) => (
    <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${choice === k ? "border-[#0B192C] bg-slate-50" : "border-slate-200"}`}>
      <input type="radio" name="refund" checked={choice === k} onChange={() => setChoice(k)} data-testid={testId} className="mt-1" />
      <span><span className="block text-sm font-semibold text-[#0B192C]">{label}</span><span className="block text-xs text-slate-500">{sub}</span></span>
    </label>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="refund-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Cancelar y reembolsar</DialogTitle>
          <DialogDescription>{booking.yacht_name} · {booking.date} · {booking.name} · pagado {fmtUSD(booking.amount)}</DialogDescription>
        </DialogHeader>
        {!q ? <Loader2 className="h-6 w-6 animate-spin text-[#00A8A0]" /> : (
          <div className="space-y-4">
            <p className="rounded-md bg-sky-50 border border-sky-200 px-3 py-2 text-xs text-sky-900" data-testid="refund-policy-note">
              Faltan <b>{q.days_before}</b> días para el zarpe → según la política aplica <b>{q.policy_pct}%</b> ({fmtUSD(q.policy_amount)}).
              {q.refunded_amount > 0 && <> Ya reembolsado: {fmtUSD(q.refunded_amount)}.</>}
              {!q.can_refund_online && <> <b>Este pago no es un cobro Whop real</b>; solo podrás registrar la cancelación.</>}
            </p>
            <div className="grid gap-2">
              {opt("policy", `Según política · ${fmtUSD(q.policy_amount)}`, "100% ≥15 días · 50% 7–14 días · 0% <7 días", "refund-opt-policy")}
              {opt("full", `Reembolso total · ${fmtUSD(q.max_refundable)}`, "Por ejemplo, mal tiempo o cancelación por parte de la empresa", "refund-opt-full")}
              {opt("none", "Cancelar sin reembolso", "Solo libera la fecha", "refund-opt-none")}
              {opt("custom", "Monto personalizado", "Reembolso parcial a tu criterio", "refund-opt-custom")}
            </div>
            {choice === "custom" && <Input type="number" min={0} max={q.max_refundable} step="0.01" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder={`Máx. ${q.max_refundable}`} data-testid="refund-custom-input" />}
            <div className="space-y-1.5"><Label>Motivo (se guarda internamente)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="refund-reason-input" /></div>
            <button onClick={submit} disabled={saving} data-testid="refund-submit-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 py-3 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}Cancelar reserva{amount > 0 ? ` y reembolsar ${fmtUSD(amount)}` : " sin reembolso"}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
